import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfigLoader } from '../config';
import type { AppConfig } from '../types/config';
import { WeatherLoader } from '../components/shared/WeatherLoader';

interface ConfigContextValue {
  config: AppConfig | null;
  isLoading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const loadedConfig = await ConfigLoader.load();
        setConfig(loadedConfig);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration';
        setError(errorMessage);
        console.error('Configuration loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  if (isLoading) {
    return (
      <div className="config-loading">
        <WeatherLoader message="Loading configuration..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="config-error">
        <h2>Configuration Error</h2>
        <p>{error}</p>
        <p>Please ensure config.json exists in the public directory with valid credentials.</p>
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </ConfigContext.Provider>
  );
};
